/**
 * adminCategoryStore — Course categories slice of the admin store.
 *
 * Endpoints:
 *   GET    /api/admin/categories
 *   POST   /api/admin/categories
 *   GET    /api/admin/categories/:id
 *   PATCH  /api/admin/categories/:id
 *   DELETE /api/admin/categories/:id
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const categoryInitialState = {
  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  categoryDetail: null,
  categoryDetailLoading: false,
  categoryDetailError: null,
  categoryMutationLoading: false,
  categoryMutationError: null,
};

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const trimmed = text.trim();
    if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

function normalizeCategories(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return data?.items || data?.categories || [];
}

export function createCategoryActions(set, get) {
  async function mutate(path, method, body, successMessage) {
    set({ categoryMutationLoading: true, categoryMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({ categoryMutationLoading: false, categoryMutationError: null, successMessage });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Action failed");
      set({ categoryMutationLoading: false, categoryMutationError: message });
      return { success: false, error: message };
    }
  }

  return {
    fetchCategories: async () => {
      set({ categoriesLoading: true, categoriesError: null });
      try {
        const data = await apiRequest(get, "/api/admin/categories");
        const categories = normalizeCategories(data);
        set({ categoriesLoading: false, categories });
        return { success: true, categories };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch categories");
        set({ categoriesLoading: false, categoriesError: message });
        return { success: false, error: message };
      }
    },

    fetchCategory: async (id) => {
      set({ categoryDetailLoading: true, categoryDetailError: null });
      try {
        const data = await apiRequest(get, `/api/admin/categories/${id}`);
        const category = data?.data ?? data ?? null;
        set({ categoryDetailLoading: false, categoryDetail: category });
        return { success: true, category };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch category");
        set({
          categoryDetailLoading: false,
          categoryDetailError: message,
          categoryDetail: null,
        });
        return { success: false, error: message };
      }
    },

    createCategory: (payload) =>
      mutate("/api/admin/categories", "POST", payload, "Category created"),

    updateCategory: (id, payload) =>
      mutate(`/api/admin/categories/${id}`, "PATCH", payload, "Category updated"),

    deleteCategory: (id) =>
      mutate(`/api/admin/categories/${id}`, "DELETE", undefined, "Category deleted"),
  };
}

const categoryActions = createCategoryActions;
export default categoryActions;
